// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsSearchContracts from '../../api/actionsSearchContracts';
// Components
import DataTable from '@/common-utils/components/renders/dataTable/DataTable';
// Common
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import mergeQuery from '@/common-utils/components/renders/dataTable/utils/mergeQuery';
import Permissions from '@/app/permissions/Permissions';
import { columnsSearchContracts, columnsSearchContractsId } from './columnDef/columnsSearchContracts';
import useSearchContractsStore from '@/app/store/modules/searchContracts';
import searchContractFields from '../../fields/searchContractFields';
import { SEARCH_CONTRACTS } from '../../api/apiUrls';

/**
 * @param root0
 * @param root0.pageActions
 * @param root0.customQuery
 * @param root0.rowsSelected
 * @param root0.setRowsSelected
 * @param root0.singleSelection
 * @param root0.filtersEventName
 * @returns {JSX} SearchContractsTable
 */
function SearchContractsTable({
    pageActions = undefined,
    rowsSelected = undefined,
    setRowsSelected = undefined,
    singleSelection = false,
    filtersEventName
}) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const {
        refetchSearchContracts: refetchFromStore,
        storeRefetchSearchContracts,
        querySearchContracts: queryStored,
        storeQuerySearchContracts: storeQuery
    } = useSearchContractsStore();

    // Constants
    const selectionMode = !!(rowsSelected && setRowsSelected);
    const QUERY_PRESETS = {
        filters: [
            {
                key: searchContractFields.PROVIDER.key,
                value: rootOrganization.uuid,
                hidden: true
            }
        ]
    };
    const initialQuery = getInitialQuery(QUERY_PRESETS);

    // State
    const [query, setQuery] = useState(mergeQuery(initialQuery, queryStored));
    const [launchRefetch, setLaunchRefetch] = useState(true);

    // Api Calls
    const { data, isFetching, refetch } = actionsSearchContracts.getSearchContracts(query, false);

    // Hooks
    useEffect(() => {
        if (rootOrganization && (launchRefetch || refetchFromStore)) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchSearchContracts(false);
        }
    }, [query, launchRefetch, refetchFromStore]);

    const allColumns = columnsSearchContracts({
        query,
        setQuery,
        queryStored,
        storeQuery,
        setLaunchRefetch,
        selectionMode,
        singleSelection,
        filtersEventName
    });

    // Render
    return (
        <DataTable
            filtersEventName={filtersEventName}
            columns={allColumns}
            data={data}
            dataFetchingIsLoading={isFetching}
            columnsId={columnsSearchContractsId}
            initialQuery={initialQuery}
            query={query}
            setQuery={setQuery}
            queryStored={queryStored}
            storeQuery={storeQuery}
            setLaunchRefetch={setLaunchRefetch}

            selectionMode={selectionMode}
            singleSelection={singleSelection}
            rowsSelected={rowsSelected}
            setRowsSelected={setRowsSelected}

            pageActions={pageActions}
            exportProps={
                {
                    permissions: [
                        Permissions.EXPORT_CONTRACTS_ACCESSES.name
                    ],
                    api: SEARCH_CONTRACTS.EXPORT
                }
            }
        />
    );
}

SearchContractsTable.propTypes = {
    pageActions: PropTypes.node,
    customQuery: PropTypes.object,
    rowsSelected: PropTypes.object,
    setRowsSelected: PropTypes.func,
    singleSelection: PropTypes.bool,
    isFromModal: PropTypes.bool,
    filtersEventName: PropTypes.string
};
export default SearchContractsTable;